/**
 * Usuários (D24): só o admin vê esta tela. Convite por e-mail via Edge Function
 * `convidar-usuario` (precisa da chave secreta, que fica só no servidor).
 */
import { useState } from "react";
import {
  BooleanField,
  BooleanInput,
  Datagrid,
  Edit,
  EmailField,
  List,
  ReferenceField,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  Title,
  Toolbar,
  TopToolbar,
  required,
  useNotify,
  useRedirect,
  Button as RaButton,
} from "react-admin";
import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextFieldMui from "@mui/material/TextField";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { supabase } from "../lib/supabase";

function AcoesLista() {
  return (
    <TopToolbar>
      <RaButton component={Link} to="/usuario/convidar" label="Convidar usuário">
        <PersonAddIcon />
      </RaButton>
    </TopToolbar>
  );
}

export function UsuarioLista() {
  return (
    <List title="Usuários" sort={{ field: "nome", order: "ASC" }} actions={<AcoesLista />} exporter={false}>
      <Datagrid bulkActionButtons={false} rowClick="edit">
        <TextField source="nome" />
        <EmailField source="email" />
        <ReferenceField source="papel_id" reference="papel" link={false}>
          <TextField source="nome" />
        </ReferenceField>
        <BooleanField source="ativo" />
        <BooleanField source="elegivel_comissao" />
      </Datagrid>
    </List>
  );
}

function BarraSalvar() {
  return (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  );
}

export function UsuarioEditar() {
  return (
    <Edit title="Editar usuário" mutationMode="pessimistic">
      <SimpleForm toolbar={<BarraSalvar />}>
        <TextInput source="nome" validate={required()} fullWidth />
        <TextInput source="email" disabled fullWidth helperText="O e-mail é o login e vem do convite." />
        <ReferenceInput source="papel_id" reference="papel">
          <SelectInput optionText="nome" validate={required()} />
        </ReferenceInput>
        <BooleanInput source="ativo" helperText="Desativado não entra mais no sistema. O histórico é mantido." />
        <BooleanInput source="elegivel_comissao" />
      </SimpleForm>
    </Edit>
  );
}

const emailValido = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** Tela de convite: chama a Edge Function, que confere se quem chama é admin. */
export function UsuarioConvidar() {
  const notify = useNotify();
  const redirect = useRedirect();
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function convidar() {
    setErro(null);
    if (!emailValido(email.trim()) || nome.trim() === "") {
      setErro("Informe nome e um e-mail válido.");
      return;
    }
    setEnviando(true);
    // O modelo de e-mail do Supabase acrescenta "auth-callback" ao endereço de retorno (padrão do ra-supabase).
    const { error } = await supabase.functions.invoke("convidar-usuario", {
      body: { email: email.trim().toLowerCase(), nome: nome.trim(), redirectTo: `${window.location.origin}/` },
    });
    setEnviando(false);
    if (error) {
      const contexto = (error as { context?: Response }).context;
      let mensagem = error.message;
      if (contexto && typeof contexto.json === "function") {
        try {
          const corpo = (await contexto.json()) as { erro?: string };
          if (corpo.erro) mensagem = corpo.erro;
        } catch {
          // resposta sem JSON: mantém a mensagem genérica
        }
      }
      setErro(mensagem);
      return;
    }
    notify(`Convite enviado para ${email.trim()}`, { type: "success" });
    redirect("list", "usuario");
  }

  return (
    <Card sx={{ mt: 2, maxWidth: 520 }}>
      <Title title="Convidar usuário" />
      <CardContent>
        <Stack spacing={2}>
          <Alert severity="info">
            A pessoa recebe um e-mail para criar a senha e entra com o papel "Usuário". Para torná-la administradora,
            edite depois.
          </Alert>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextFieldMui label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} required fullWidth />
          <TextFieldMui
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={convidar} disabled={enviando}>
              {enviando ? "Enviando…" : "Enviar convite"}
            </Button>
            <Button component={Link} to="/usuario">
              Cancelar
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
